import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import {
  type CallSession,
  CallSessionApi,
  type Campaign,
  CampaignApi,
  type CreateCallSessionDto,
  type CreateCampaignFromFileRequest,
  type CreatePrivateCampaignDto,
  type UpdateCallSessionDto,
  type UpdateCampaignDto,
} from '@/api-swagger'
import { useUserStore } from '@/stores/useUserStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useRouter } from 'vue-router'
import { withApiState } from '@/utils/withApiState'

export const useCampaignStore = defineStore('campaign', () => {
  const privateCampaigns = ref<Campaign[] | null>(null)
  const managerCampaigns = ref<Campaign[] | null>(null)
  const callSessions = ref<CallSession[]>([])
  const loadingCampaign = ref(false)
  const loadingCallSessions = ref(false)
  const loadingCreate = ref(false)
  const loadingUpdate = ref(false)
  const loadingAddCallSessions = ref(false)
  const loadingDispatch = ref(false)
  const loadingDelete = ref(false)
  const error = ref<string | null>(null)
  const api = new CampaignApi()
  const apiCallSession = new CallSessionApi()
  const siretsSummary = ref<{
    allExist: boolean
    existingCount: number
    toCreateCount: number
  } | null>(null)

  const userStore = useUserStore()
  const router = useRouter()

  const { userConnected } = storeToRefs(userStore)

  const getManagerCampaigns = async () => {
    loadingCampaign.value = true
    loadingCallSessions.value = true
    managerCampaigns.value = null
    try {
      if (userConnected && userConnected.value) {
        await api
          .getManagerCampaigns()
          .then((res) => {
            managerCampaigns.value = res
          })
          .catch((error) => {
            console.error(error)
          })
        await api
          .getMyManagerCampaigns()
          .then((res) => {
            const existing = (managerCampaigns.value ?? []) as Campaign[]
            const existingIds = new Set(existing.map((c) => c.id))
            const newCampaigns = res.filter((c) => !existingIds.has(c.id))
            managerCampaigns.value = [...existing, ...newCampaigns]
          })
          .catch((error) => {
            console.error(error)
          })
      }
      loadingCampaign.value = false
    } catch (error) {
      console.error(error)
    } finally {
      loadingCampaign.value = false
    }
  }

  const getCallSessionByCampaignId = async (campaignId: number) => {
    loadingCallSessions.value = true
    callSessions.value = []
    await apiCallSession
      .getByCampaignId({ campaignId: campaignId })
      .then((res) => {
        callSessions.value = res
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        loadingCallSessions.value = false
      })
    return callSessions.value
  }

  const createMyCampaign = async (createCampagneDto: CreatePrivateCampaignDto) => {
    if (!userConnected.value) {
      const boostCoreStore = useBoostCorePermissionStore()
      const email = boostCoreStore.user?.email
      if (email) await userStore.getUserByEmail(email)
    }
    if (userConnected.value) {
      loadingCreate.value = true
      await api
        .createCampaign({ createPrivateCampaignDto: createCampagneDto })
        .catch((error) => console.error(error))
        .finally(() => {
          loadingCreate.value = false
          getMyPrivateCampaign()
        })
    }
  }

  const createCallSessions = async (callSessions: CreateCallSessionDto[]) => {
    loadingAddCallSessions.value = true
    await apiCallSession
      .createCallSessions({
        createCallSessionDto: callSessions,
      })
      .catch((error) => console.error(error))
    getMyPrivateCampaign()

    loadingAddCallSessions.value = false
  }

  const removeCallSession = async (id: number) => {
    await apiCallSession
      .deleteCallSession({
        callSessionId: id,
      })
      .catch((error) => console.error(error))
    getMyPrivateCampaign()
  }

  const validateCallSession = async (id: number) => {
    const updateCallSessionDto: UpdateCallSessionDto = {
      status: 'done',
    }

    await apiCallSession
      .updateCallSession({
        callSessionId: id,
        updateCallSessionDto: updateCallSessionDto,
      })
      .catch((error) => console.error(error))
      .finally(() => {
        getMyPrivateCampaign()
      })
  }

  async function updateCampaign(campaignId: number, campaign: UpdateCampaignDto) {
    loadingUpdate.value = true

    try {
      await api
        .updateCampaign({ campaignId: campaignId, updateCampaignDto: campaign })
        .catch((error) => console.error(error))
        .finally(() => {
          getManagerCampaigns()
          getMyPrivateCampaign()
        })
    } catch (error) {
      console.error(error)
    } finally {
      loadingUpdate.value = false
    }
  }

  async function deleteCampaignById(campaignId: number) {
    loadingUpdate.value = true

    try {
      await api
        .deleteCampaignById({ campaignId: campaignId })
        .catch((error) => console.error(error))
      getManagerCampaigns()
      getMyPrivateCampaign()
      await router.push('/campaign')
    } catch (error) {
      console.error(error)
    } finally {
      loadingUpdate.value = false
    }
  }

  async function deleteCalls(campaignId: number, calls: number[]) {
    loadingDelete.value = true

    try {
      await apiCallSession
        .deleteCallSessionsByIds({
          deleteCallSessionsByIdsRequest: { campaignId: campaignId, callSessionIds: calls },
        })
        .catch((error) => console.error(error))
        .finally(() => {
          getMyPrivateCampaign()
          getCallSessionByCampaignId(campaignId)
          getManagerCampaigns()
        })
    } catch (error) {
      console.error(error)
    } finally {
      loadingUpdate.value = false
    }
  }

  async function createCampaignFromFile(
    file: File,
    data: { dto: CreatePrivateCampaignDto; participantIds: number[] },
  ): Promise<Campaign> {
    return (await withApiState(
      async () => {
        const requestParameters: CreateCampaignFromFileRequest = {
          file: file,
          createCampaignDto: JSON.stringify(data.dto),
          participantIds: JSON.stringify(data.participantIds),
        }

        const response = await api.createCampaignFromFile(requestParameters)
        await getManagerCampaigns()
        return response[0]
      },
      { logLabel: 'Erreur lors de la création de la campagne', rethrow: true },
    )) as Campaign
  }

  async function getMyPrivateCampaign() {
    if (userConnected.value) {
      privateCampaigns.value = await api.getPrivateCampaigns()
    }
  }

  async function dispatchCallSessions(
    callSessionIds: number[],
    userIds: number[],
    campaignId: number,
  ) {
    loadingDispatch.value = true
    try {
      return await apiCallSession
        .dispatchCallSessions({
          dispatchCallSessionsDto: {
            callSessionIds,
            userIds,
            campaignId,
          },
        })
        .catch((error) => console.error(error))
        .finally(() => {
          getMyPrivateCampaign()
          getCallSessionByCampaignId(campaignId)
          getManagerCampaigns()
        })
    } catch (error) {
      console.error(error)
    } finally {
      loadingDispatch.value = false
    }
  }

  return {
    privateCampaigns,
    managerCampaigns,
    loadingCampaign,
    loadingCallSessions,
    loadingAddCallSessions,
    loadingUpdate,
    loadingDispatch,
    loadingDelete,
    siretsSummary,
    callSessions,
    error,
    deleteCalls,
    createMyCampaign,
    getMyPrivateCampaign,
    updateCampaign,
    deleteCampaignById,
    getManagerCampaigns,
    removeCallSession,
    getCallSessionByCampaignId,
    createCallSessions,
    validateCallSession,
    createCampaignFromFile,
    dispatchCallSessions,
  }
})
